import { useCallback, useEffect, useRef, useState } from "react";
import { completeAttempt } from "../api/completeAttempt";
import { editCompletedAttempt } from "../api/editCompletedAttempt";
import { fetchAttemptForReview } from "../api/fetchAttemptForReview";
import { saveCoachResponses } from "../api/saveCoachResponses";
import { absorbRejection } from "../utils/rejections";
import type {
  CoachAttemptDetail,
  SaveCoachResponsesPayload,
} from "../types/diagnostic";

/**
 * The fill screen's attempt: load, save, complete, correct.
 *
 * `hydrationKey` is the important part. The screen holds the coach's answers
 * in local state and seeds them from the attempt; if that seeding ran whenever
 * the attempt object changed, a save landing mid-sentence would replace the
 * attempt and wipe out everything typed while the request was in flight. The
 * key only advances on a *fetch* — the initial load and an explicit reload —
 * so a save response updates the stored attempt without touching the form.
 */
export function useDiagnosticAttempt(attemptId: number) {
  const [attempt, setAttempt] = useState<CoachAttemptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [hydrationKey, setHydrationKey] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // A different attempt is a different record. Held over, the previous one
  // renders under the new address for the length of the fetch — `loading &&
  // !attempt` is false while `attempt` is the old one, so the fill screen
  // showed another company's answers, and a save from that screen would have
  // written them to whichever attempt the URL now names. Cleared here rather
  // than in `reload()`, which also runs after a correction and must not blank
  // the screen the coach is working in.
  useEffect(() => {
    setAttempt(null);
  }, [attemptId]);

  const reload = useCallback(() => {
    // A junk URL segment reaches here as `NaN`. There is nothing to fetch, but
    // an early return still has to settle the state it owns: `loading` starts
    // true, so returning bare left every reader of it on a skeleton for ever,
    // waiting on a request that was never made. Any request already in flight
    // belonged to the *previous* id and must not land under this one.
    if (!Number.isFinite(attemptId)) {
      cancelPendingRequest();
      setLoading(false);
      return;
    }
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    fetchAttemptForReview(attemptId, controller.signal)
      .then((loaded) => {
        setAttempt(loaded);
        // Only a fetch re-seeds the form.
        setHydrationKey((key) => key + 1);
      })
      .catch(absorbRejection)
      .finally(() => {
        // A cancelled request does not own the loading flag. It settles
        // *after* the request that superseded it has already set the flag, so
        // clearing it unconditionally hid the spinner for a fetch that was
        // still in flight.
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [attemptId, cancelPendingRequest]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => cancelPendingRequest, [cancelPendingRequest]);

  const save = useCallback(
    (payload: SaveCoachResponsesPayload) => {
      setSaving(true);
      return saveCoachResponses(attemptId, payload)
        .then((saved) => {
          setAttempt(saved);
          return saved;
        })
        .finally(() => setSaving(false));
    },
    [attemptId],
  );

  /**
   * Save, then complete. Completion is refused server-side unless every
   * applicable question is answered, so the answers have to be stored first.
   */
  const saveAndComplete = useCallback(
    (payload: SaveCoachResponsesPayload) => {
      setCompleting(true);
      return saveCoachResponses(attemptId, payload)
        .then(() =>
          completeAttempt(attemptId, { coachNotes: payload.coachNotes }),
        )
        .then((completed) => {
          setAttempt(completed);
          return completed;
        })
        .finally(() => setCompleting(false));
    },
    [attemptId],
  );

  /** Correct a completed attempt, writing an audit trail. */
  const saveCorrection = useCallback(
    (payload: SaveCoachResponsesPayload) => {
      setSaving(true);
      return editCompletedAttempt(attemptId, payload)
        .then((updated) => {
          setAttempt(updated);
          return updated;
        })
        .finally(() => setSaving(false));
    },
    [attemptId],
  );

  return {
    attempt,
    loading,
    saving,
    completing,
    hydrationKey,
    reload,
    save,
    saveAndComplete,
    saveCorrection,
  };
}
